import { redirect } from '@tanstack/react-router'
import { updateSecurityContext } from '@/contexts/SecurityContext'
import { AppLogger } from '@/lib/logger'
import { getCookieActive, removeCookieActive } from '@/lib/cookieManager'
import { Conex360StorageManager } from '@/lib/storageManager'
import { parseJwtPayload } from '@/lib/tokenManager'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Types (from docs/functionalRequirements/securityManagement.md)
// ============================================================================

export interface SessionInfo {
  sessionToken: string
}

export interface UserInfo {
  userNumber: number
  userFirstName: string
  userLastName: string
  userEmail?: string
  userInitials: string
  userDarkMode: boolean
  userRole?: string  // User's role (e.g., 'admin', 'manager', 'viewer')
}

export interface RouteAccessConfig {
  title?: string
  isSecured: boolean
  accessTo: string[]  // Array of roles that have access
}

export interface RoutesConfig {
  routes: Record<string, RouteAccessConfig>
  defaults: {
    isSecured: boolean
    accessTo: string[]
  }
}

/**
 * SessionObject interface for two-factor session state tracking
 *
 * This object tracks the current session state with both validity status
 * and time-based information. Used by SecurityVerifier to determine if
 * a user's session is still valid.
 */
export interface SessionObject {
  /** Whether the session is currently valid (both factors passed) */
  isValid: boolean

  /** Timestamp (milliseconds) of the last successful verification */
  lastActivity: number

  /** Timestamp (milliseconds) when the session expires (from token exp claim) */
  expiresAt: number

  /** Seconds remaining until token expiration */
  tokenExpiresIn: number

  /** Timestamp (milliseconds) when the token was last refreshed */
  refreshedAt: number
}

export interface GlobalContext {
  isUserLoggedIn: boolean
  isSecuredRoute: boolean
  sessionInfo?: SessionInfo
  userInfo?: UserInfo
  userRole?: string  // Current user's role
  currentRouteTitle: string
  routeAccessConfig?: RoutesConfig  // Cached route access configuration

  // Session verification fields (from Task Group 2)
  sessionObject?: SessionObject  // Current session state
  isSessionTerminated?: boolean  // Flag to prevent retry loops
  sessionTerminationReason?: string  // Reason code for audit trail
}

// ============================================================================
// Helpers
// ============================================================================

const SESSION_STORAGE_KEY = 'sessionInfo'
const USER_STORAGE_KEY = 'userInfo'
const CONEX_JWT_KEY = 'conExJwt'
const SESSION_ID_KEY = 'sessionId'

function loadSessionInfo(): SessionInfo | undefined {
  if (typeof window === 'undefined') return undefined

  try {
    const raw =
      window.sessionStorage.getItem(SESSION_STORAGE_KEY) ||
      window.localStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as SessionInfo
    if (!parsed?.sessionToken) return undefined
    return parsed
  } catch {
    return undefined
  }
}

export function loadUserInfo(): UserInfo | undefined {
  if (typeof window === 'undefined') return undefined

  try {
    const raw =
      window.sessionStorage.getItem(USER_STORAGE_KEY) ||
      window.localStorage.getItem(USER_STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as UserInfo
    if (!parsed?.userNumber) return undefined
    return parsed
  } catch {
    return undefined
  }
}

/**
 * Get ConEx JWT from sessionStorage
 */
function getConExJwt(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(CONEX_JWT_KEY) || window.localStorage.getItem(CONEX_JWT_KEY) || null
  } catch {
    return null
  }
}

/**
 * Validate JWT structure (basic validation - has 3 parts)
 */
function isValidJwtStructure(token: string): boolean {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return false
    }
    // All parts should be non-empty base64-encoded strings
    return parts.every(part => part.length > 0)
  } catch {
    return false
  }
}

/**
 * Clear session storage on failed verification
 */
function clearSessionStorage(): void {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY)
    window.sessionStorage.removeItem(CONEX_JWT_KEY)
    window.sessionStorage.removeItem(SESSION_ID_KEY)
    window.localStorage.removeItem(SESSION_STORAGE_KEY)
    window.localStorage.removeItem(CONEX_JWT_KEY)
    window.localStorage.removeItem(SESSION_ID_KEY)
  } catch (error) {
    log.error('Failed to clear sessionStorage:', error)
  }
}

// ============================================================================
// Route Access Configuration Cache
// ============================================================================

let routesConfigCache: RoutesConfig | null = null

/**
 * Load route access configuration (cached after first load)
 * Reads from window.__conexRouteAccessConfig set by ConfigContext.
 * This runs in TanStack Router's beforeLoad hook (outside React render),
 * so React context is not available - window global is required.
 */
function loadRoutesConfig(): RoutesConfig {
  if (routesConfigCache) {
    return routesConfigCache
  }

  try {
    if (typeof window !== 'undefined') {
      const routeConfig = (window as any).__conexRouteAccessConfig as RoutesConfig | undefined
      if (routeConfig?.routes && routeConfig?.defaults) {
        routesConfigCache = routeConfig
        return routeConfig
      }
    }
  } catch (error) {
    log.error('Failed to load routes config:', error)
  }

  // Return default config if loading fails or is not yet available.
  return {
    routes: {},
    defaults: {
      isSecured: true,
      accessTo: [],
    },
  }
}

/**
 * Get route access configuration for a specific pathname
 */
function getRouteConfig(pathname: string, routesConfig: RoutesConfig): RouteAccessConfig {
  // Try exact match first
  if (routesConfig.routes[pathname]) {
    return routesConfig.routes[pathname]
  }

  // Try matching route patterns (e.g., /sensory/devices/$deviceNumber)
  for (const [routePath, config] of Object.entries(routesConfig.routes)) {
    // Escape regex metacharacters in path segments, then replace param placeholders
    const escaped = routePath
      .replace(/\$[\w]+/g, '\x00')
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      .replace(/\x00/g, '[^/]+')
    const regex = new RegExp(`^${escaped}$`)
    if (regex.test(pathname)) {
      return config
    }
  }

  // Return defaults if no match found
  return {
    isSecured: routesConfig.defaults.isSecured,
    accessTo: routesConfig.defaults.accessTo,
  }
}

/**
 * Check if user role has access to a route
 */
function hasRouteAccess(userRole: string | undefined, routeConfig: RouteAccessConfig): boolean {
  // If route is not secured, allow access
  if (!routeConfig.isSecured) {
    return true
  }

  // Legacy job-role removal (intermediary stage before RBAC):
  // Every route in app_routes was linked to the admin job role, and this check only
  // ever tested for that role. With job_roles / user_job_roles removed on the backend,
  // the front-end no longer gates on the job role pulled from the token / session
  // storage (falling back to local storage) — any user who passed two-factor
  // verification is allowed through. Restore the role-based check below once RBAC /
  // resource roles land.
  void userRole
  return true

  // // If no roles are configured for this route, allow any authenticated user
  // // (two-factor verification already passed at this point)
  // if (!routeConfig.accessTo || routeConfig.accessTo.length === 0) {
  //   return true
  // }
  //
  // // If no user role available, deny access to role-restricted routes
  // if (!userRole) {
  //   return false
  // }
  //
  // // Compare roles case-insensitively to handle API/config casing differences
  // const normalizedUserRole = userRole.trim().toLowerCase()
  // const normalizedAllowedRoles = routeConfig.accessTo.map((role) => role.trim().toLowerCase())
  //
  // // Check if user role is in accessTo array
  // return normalizedAllowedRoles.includes(normalizedUserRole)
}

// ============================================================================
// Two-Factor Security Verification
// ============================================================================

/**
 * Factor 1: Cookie Check
 * - Verifies conex_session_active cookie exists
 * - Returns true if cookie found, false otherwise
 */
function checkCookieFactor(): boolean {
  return getCookieActive()
}

/**
 * Factor 2: ConEx JWT Verification
 * - Gets ConEx JWT from sessionStorage
 * - Performs basic structure validation (3 parts separated by dots)
 * - Checks token expiration with 30-second clock skew allowance
 * - Returns true if JWT valid, false otherwise
 */
function checkJwtFactor(): boolean {
  const jwt = getConExJwt()

  if (!jwt) {
    return false
  }

  if (!isValidJwtStructure(jwt)) {
    log.warn('Invalid JWT structure detected')
    return false
  }

  // Check token expiration
  const payload = parseJwtPayload(jwt)
  if (payload?.exp) {
    const nowSeconds = Math.floor(Date.now() / 1000)
    // Allow 30 seconds of clock skew
    if (payload.exp < nowSeconds - 30) {
      log.warn('JWT token has expired', {
        expiredAt: new Date(payload.exp * 1000).toISOString(),
        now: new Date().toISOString(),
      })
      return false
    }
  }

  return true
}

/**
 * Two-factor verification for secured routes
 * - Factor 1: Cookie present (fast check)
 * - Factor 2: JWT valid (structure check)
 * - Returns true only if BOTH factors pass
 */
function twoFactorVerification(): boolean {
  const cookieValid = checkCookieFactor()

  // If cookie missing, Factor 1 fails - return false
  if (!cookieValid) {
    log.debug('Factor 1 (cookie) verification failed')
    return false
  }

  // Factor 1 passed, check Factor 2
  const jwtValid = checkJwtFactor()

  // If JWT invalid, Factor 2 fails - return false
  if (!jwtValid) {
    log.debug('Factor 2 (JWT) verification failed')
    clearSessionStorage()
    return false
  }

  // Both factors passed
  return true
}

// ============================================================================
// Security Verifier
// ============================================================================

/**
 * Security verifier used from root route `beforeLoad` function.
 *
 * This function:
 * 1. Loads route access configuration (cached after first load)
 * 2. Gets route title from the deepest matched route's staticData
 * 3. Implements two-factor verification (cookie + JWT) for secured routes
 * 4. Checks if authenticated user's role has access to the route
 * 5. Redirects if access is denied
 * 6. Updates GlobalContext with security state
 *
 * @param ctx - TanStack Router context
 * @param options - Optional configuration
 * @param options.isSecured - If false, bypasses all security checks (useful for testing)
 */
export async function SecurityVerifier(
  ctx: any,
  options?: { isSecured?: boolean }
): Promise<GlobalContext> {
  const pathname: string = ctx.location?.pathname ?? ''
  const loginPath = '/signin'

  // If security is disabled at root level, bypass all checks
  const rootSecurityEnabled = options?.isSecured !== false

  // Load route access configuration (cached)
  const routesConfig = loadRoutesConfig()

  // Get route configuration for current pathname
  const routeConfig = getRouteConfig(pathname, routesConfig)

  // Get route metadata from deepest matched route's staticData
  const matches = ctx.matches || []
  const deepestMatch = matches.length > 0 ? matches[matches.length - 1] : undefined
  // TanStack Router exposes staticData at multiple paths depending on route type
  const routeStaticData = (deepestMatch?.staticData as any)
    ?? (deepestMatch?.route?.options?.staticData as any)
    ?? (deepestMatch?.route?.staticData as any)
    ?? {}
  let currentRouteTitle: string = routeConfig.title || ''

  // Try to get title from deepest route match
  if (routeStaticData.title) {
    currentRouteTitle = routeStaticData.title
  }

  // Fallback: use route config title if no route match found
  if (!currentRouteTitle && routeConfig.title) {
    currentRouteTitle = routeConfig.title
  }

  // Format the document title with app name prefix
  const formattedTitle = currentRouteTitle ? `ConEx 360 | ${currentRouteTitle}` : 'ConEx 360'

  const staticIsSecured =
    typeof routeStaticData.isSecured === 'boolean' ? routeStaticData.isSecured : undefined

  // Always treat sign-in route as unsecured.
  // Otherwise prefer route staticData flag, then fallback to YAML config.
  const isSecuredRoute: boolean =
    pathname === loginPath ? false : (staticIsSecured ?? routeConfig.isSecured)

  // Load authentication state (for backward compatibility)
  const sessionInfo = loadSessionInfo()
  const userInfo = loadUserInfo()
  const userRole = userInfo?.userRole
  const isUserLoggedIn = Boolean(sessionInfo?.sessionToken && userInfo)

  // Skip all security checks if root security is disabled
  if (!rootSecurityEnabled) {
    // Build GlobalContext without security enforcement
    const globalContext: GlobalContext = {
      isUserLoggedIn,
      isSecuredRoute: false, // Treat as unsecured when security is disabled
      sessionInfo,
      userInfo,
      userRole,
      currentRouteTitle,
      routeAccessConfig: routesConfig,
    }

    // Update context
    if (typeof window !== 'undefined') {
      updateSecurityContext(globalContext)
      if (import.meta.env.DEV) {
        ;(window as any).__conexSecurityContext = globalContext
      }
      if (typeof document !== 'undefined') {
        document.title = formattedTitle
      }
    }

    if (ctx.context) {
      ctx.context.security = globalContext
    }

    return globalContext
  }

  // Prevent authenticated users from visiting the sign-in page
  if (!isSecuredRoute && isUserLoggedIn && pathname === loginPath) {
    throw redirect({
      to: '/home',
      replace: true,
    })
  }

  // Enforce security on secured routes
  if (isSecuredRoute) {
    // Capture state BEFORE verification (twoFactorVerification clears JWT on failure)
    const preVerifyCookie = getCookieActive()
    const preVerifyJwtSession = typeof window !== 'undefined' ? window.sessionStorage.getItem(CONEX_JWT_KEY) : null
    const preVerifyJwtLocal = typeof window !== 'undefined' ? window.localStorage.getItem(CONEX_JWT_KEY) : null

    // Implement two-factor verification for secured routes
    const verificationPassed = twoFactorVerification()

    if (!verificationPassed) {
      log.error('SecurityVerifier: two-factor verification failed (cookie or JWT invalid), redirecting to signin', { pathname })

      if (typeof window !== 'undefined') {
        // Persist failure reason across the hard reload so signin page can display it
        try {
          let loginDebug: unknown = null
          try { loginDebug = JSON.parse(window.localStorage.getItem('__conexLoginDebug') ?? 'null') } catch { /* ignore */ }
          const debugPayload = JSON.stringify({
            reason: 'two-factor-failed',
            pathname,
            preVerify: {
              hasCookie: preVerifyCookie,
              hasJwtInSession: !!preVerifyJwtSession,
              hasJwtInLocal: !!preVerifyJwtLocal,
              jwtLength: (preVerifyJwtSession || preVerifyJwtLocal)?.length ?? 0,
            },
            loginDebug,
            ts: Date.now(),
          })
          window.sessionStorage.setItem('__conexSecurityDebug', debugPayload)
          window.localStorage.setItem('__conexSecurityDebug', debugPayload)
        } catch { /* ignore */ }

        // Full page reload clears stale React state (AuthContext keeps isUserLoggedIn:true
        // in memory across SPA navigations even after storage is cleared on session expiry).
        // A hard reload forces AuthContext to re-initialize from the now-empty storage,
        // preventing SignInPage from immediately bouncing back to /home.
        window.location.replace(loginPath)
      }

      // Fallback for SSR environments
      throw redirect({ to: loginPath, replace: true })
    }

    // Check role-based access (if both factors passed)
    if (!hasRouteAccess(userRole, routeConfig)) {
      const fallbackRoute = pathname === '/home' ? '/signin' : '/home'

      // Persist failure reason across the redirect so signin page can display it
      if (typeof window !== 'undefined') {
        try {
          let storedUserInfo: any = null
          try { storedUserInfo = JSON.parse(window.sessionStorage.getItem('userInfo') || window.localStorage.getItem('userInfo') || 'null') } catch { /* ignore */ }
          const debugPayload = JSON.stringify({
            reason: 'role-check-failed',
            pathname,
            userRole,
            requiredRoles: routeConfig.accessTo,
            storedUserInfo,
            ts: Date.now(),
          })
          window.sessionStorage.setItem('__conexSecurityDebug', debugPayload)
          window.localStorage.setItem('__conexSecurityDebug', debugPayload)
        } catch { /* ignore */ }
      }

      log.error('SecurityVerifier: role check failed — redirecting', {
        pathname,
        userRole,
        requiredRoles: routeConfig.accessTo,
        fallbackRoute,
      })

      // If falling back to /signin, clear the session first to prevent an infinite
      // redirect loop: an authenticated user on /signin gets redirected to /home,
      // which would redirect back to /signin, etc.
      if (fallbackRoute === '/signin') {
        clearSessionStorage()
        removeCookieActive()
      }

      throw redirect({
        to: fallbackRoute,
        replace: true,
      })
    }
  }

  // Build GlobalContext
  const globalContext: GlobalContext = {
    isUserLoggedIn,
    isSecuredRoute,
    sessionInfo,
    userInfo,
    userRole,
    currentRouteTitle,
    routeAccessConfig: routesConfig,  // Include config in context for components
  }

  // Store GlobalContext in multiple places for access:
  // 1. Update React context via module-level update function (efficient, no polling)
  if (typeof window !== 'undefined') {
    // Direct call to update function - no polling needed
    updateSecurityContext(globalContext)

    // Also store on window for debugging (development only)
    if (import.meta.env.DEV) {
      ;(window as any).__conexSecurityContext = globalContext
    }

    // Basic document title update using the formatted route title
    if (typeof document !== 'undefined') {
      document.title = formattedTitle
    }

    // Update conex360 storage with navigation information
    if (isUserLoggedIn && userInfo) {
      const fullName = `${userInfo.userFirstName} ${userInfo.userLastName}`
      Conex360StorageManager.updateRouteNavigation(pathname, fullName)
    }
  }

  // 2. TanStack Router context for access via useRouteContext()
  if (ctx.context) {
    ctx.context.security = globalContext
  }

  return globalContext
}
